import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Grid,
    Group,
    Loader,
    NumberInput,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";

import { getAuthHeaders } from "../../../api/authHelper";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

interface EventDto {
    eventID: number;
    eventName: string;
    startTime?: string;
    endTime?: string;
}

interface OptionDto {
    value: number;
    text: string;
    eventid?: number;
}

interface LocationSetup {
    practiceLocationId: number;
    numberOfCourts: number;
}

interface CreatePlanResponse {
    practicePlanId: number;
}

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const PracticePlannerCreatePage: React.FC = () => {
    const navigate = useNavigate();

    const apiBase = process.env.REACT_APP_API_URL;

    const EVENTS_URL = `${apiBase}/api/Events`;
    const LOCATIONS_URL = `${apiBase}/api/Option_PracticeLocation`;
    const PLANNER_URL = `${apiBase}/api/practice-planner`;

    // ---------------------------------------------------------
    // STATE
    // ---------------------------------------------------------

    const [events, setEvents] = useState<EventDto[]>([]);
    const [locations, setLocations] = useState<OptionDto[]>([]);

    const [selectedEventId, setSelectedEventId] =
        useState<string | null>(null);

    /*
     * Bruger native date input.
     * Dermed behøver siden ikke Mantine DateInput.
     */
    const [practiceDate, setPracticeDate] = useState("");

    const [locationSetup, setLocationSetup] =
        useState<LocationSetup[]>([]);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [saving, setSaving] = useState(false);

    const [error, setError] = useState<string | null>(null);

    // ---------------------------------------------------------
    // LOAD EVENTS
    // ---------------------------------------------------------

    useEffect(() => {
        fetchEvents();
    }, []);

    // ---------------------------------------------------------
    // EVENT CHANGED
    // ---------------------------------------------------------

    useEffect(() => {
        if (!selectedEventId) {
            setLocations([]);
            setLocationSetup([]);
            return;
        }

        fetchLocations(Number(selectedEventId));
    }, [selectedEventId]);

    // ---------------------------------------------------------
    // FETCH EVENTS
    // ---------------------------------------------------------

    const fetchEvents = async () => {
        try {
            setLoadingEvents(true);
            setError(null);

            const res = await fetch(EVENTS_URL, {
                headers:
                    getAuthHeaders() as Record<string, string>,
            });

            if (!res.ok) {
                throw new Error(
                    `Kunne ikke hente events (${res.status})`
                );
            }

            const data = await res.json();

            const eventList: EventDto[] = Array.isArray(data)
                ? data
                : data.items ?? data.events ?? [];

            setEvents(eventList);

            /*
             * Samme princip som din eksisterende admin-side:
             * vælg første event automatisk.
             */
            if (eventList.length > 0) {
                setSelectedEventId(
                    String(eventList[0].eventID)
                );
            }
        } catch (err) {
            console.error(err);

            setError("Kunne ikke hente events.");
        } finally {
            setLoadingEvents(false);
        }
    };

    // ---------------------------------------------------------
    // FETCH LOCATIONS
    // ---------------------------------------------------------

    const fetchLocations = async (eventId: number) => {
        try {
            setLoadingLocations(true);
            setError(null);

            const res = await fetch(
                `${LOCATIONS_URL}?eventId=${eventId}`,
                {
                    headers:
                        getAuthHeaders() as Record<string, string>,
                }
            );

            if (!res.ok) {
                throw new Error(
                    `Kunne ikke hente træningssteder (${res.status})`
                );
            }

            const data: OptionDto[] = await res.json();

            const locationList =
                Array.isArray(data) ? data : [];

            setLocations(locationList);

            /*
             * Opret automatisk setup for alle locations.
             * De starter med 0 baner.
             */
            setLocationSetup(
                locationList.map((location) => ({
                    practiceLocationId: location.value,
                    numberOfCourts: 0,
                }))
            );
        } catch (err) {
            console.error(err);

            setLocations([]);
            setLocationSetup([]);

            setError(
                "Kunne ikke hente træningssteder for eventet."
            );
        } finally {
            setLoadingLocations(false);
        }
    };

    // ---------------------------------------------------------
    // SELECTED EVENT
    // ---------------------------------------------------------

    const selectedEvent = useMemo(() => {
        if (!selectedEventId) {
            return undefined;
        }

        return events.find(
            (event) =>
                String(event.eventID) === selectedEventId
        );
    }, [events, selectedEventId]);

    // ---------------------------------------------------------
    // ACTIVE LOCATIONS
    // ---------------------------------------------------------

    const activeLocations = useMemo(() => {
        return locationSetup.filter(
            (location) => location.numberOfCourts > 0
        );
    }, [locationSetup]);

    // ---------------------------------------------------------
    // TOTAL COURTS
    // ---------------------------------------------------------

    const totalCourts = useMemo(() => {
        return activeLocations.reduce(
            (total, location) =>
                total + location.numberOfCourts,
            0
        );
    }, [activeLocations]);

    // ---------------------------------------------------------
    // UPDATE LOCATION COURTS
    // ---------------------------------------------------------

    const updateLocationCourts = (
        practiceLocationId: number,
        value: string | number
    ) => {
        const numberOfCourts =
            typeof value === "number"
                ? value
                : Number(value) || 0;

        setLocationSetup((current) =>
            current.map((location) =>
                location.practiceLocationId ===
                    practiceLocationId
                    ? {
                        ...location,
                        numberOfCourts:
                            Math.max(0, numberOfCourts),
                    }
                    : location
            )
        );
    };

    // ---------------------------------------------------------
    // GET COURT COUNT
    // ---------------------------------------------------------

    const getCourtCount = (
        practiceLocationId: number
    ) => {
        return (
            locationSetup.find(
                (x) =>
                    x.practiceLocationId ===
                    practiceLocationId
            )?.numberOfCourts ?? 0
        );
    };

    // ---------------------------------------------------------
    // VALIDATE
    // ---------------------------------------------------------

    const validate = () => {
        if (!selectedEventId) {
            setError("Vælg et event.");
            return false;
        }

        if (!practiceDate) {
            setError("Vælg en dato.");
            return false;
        }

        if (activeLocations.length === 0) {
            setError(
                "Angiv mindst én hal med mindst 1 bane."
            );

            return false;
        }

        return true;
    };

    // ---------------------------------------------------------
    // CREATE PLAN
    // ---------------------------------------------------------

    const createPlan = async () => {
        if (!validate()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const payload = {
                eventId: Number(selectedEventId),

                practiceDate,

                locations: activeLocations.map(
                    (location) => ({
                        practiceLocationId:
                            location.practiceLocationId,

                        numberOfCourts:
                            location.numberOfCourts,
                    })
                ),
            };

            console.log(
                "CREATE PRACTICE PLAN",
                payload
            );

            const res = await fetch(
                PLANNER_URL,
                {
                    method: "POST",

                    headers:
                        getAuthHeaders({
                            "Content-Type":
                                "application/json",
                        }) as Record<string, string>,

                    body: JSON.stringify(payload),
                }
            );

            if (!res.ok) {
                const message = await res.text();

                throw new Error(
                    message ||
                    `Kunne ikke oprette planen (${res.status})`
                );
            }

            const data: CreatePlanResponse =
                await res.json();

            if (!data.practicePlanId) {
                throw new Error(
                    "Planen blev oprettet, men API'et returnerede ikke PracticePlanId."
                );
            }

            notifications.show({
                title: "Træningsplan oprettet",
                message:
                    "Planen er klar til planlægning.",
                color: "green",
            });

            navigate(
                `/Admin/PracticePlanner/${selectedEventId}/${data.practicePlanId}`
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.message ||
                "Kunne ikke oprette træningsplanen."
            );
        } finally {
            setSaving(false);
        }
    };

    // ---------------------------------------------------------
    // FORMAT EVENT DATE
    // ---------------------------------------------------------

    const formatEventDate = (value?: string) => {
        if (!value) {
            return "";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleDateString("da-DK");
    };

    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (
        <div>
            <HeroSectionV2
                titleEn="Create training plan"
                titleDa="Opret træningsplan"
                descriptionEn="Create the daily training schedule"
                descriptionDa="Opsæt haller og banekapacitet for træningsdagen"
                imageSrc={Billede1}
            />

            <Container size="lg" py="xl">
                <Stack gap="lg">
                    {/* HEADER */}

                    <Group
                        justify="space-between"
                        align="flex-start"
                    >
                        <div>
                            <Title order={1}>
                                Opret træningsplan
                            </Title>

                            <Text c="dimmed">
                                Vælg event og dato, og angiv hvor
                                mange baner der er til rådighed i
                                hver hal.
                            </Text>
                        </div>

                        <Button
                            variant="default"
                            onClick={() =>
                                navigate("/Admin/Practice")
                            }
                        >
                            Tilbage
                        </Button>
                    </Group>

                    {/* ERROR */}

                    {error && (
                        <Alert
                            color="red"
                            title="Fejl"
                            withCloseButton
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* EVENT + DATE */}

                    <Paper
                        withBorder
                        radius="md"
                        p="lg"
                    >
                        <Stack gap="lg">
                            <div>
                                <Title order={3}>
                                    1. Vælg dag
                                </Title>

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Vælg hvilket event og hvilken
                                    træningsdag planen gælder for.
                                </Text>
                            </div>

                            <Grid>
                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6,
                                    }}
                                >
                                    <Select
                                        label="Event"
                                        placeholder={
                                            loadingEvents
                                                ? "Henter events..."
                                                : "Vælg event"
                                        }
                                        searchable
                                        clearable={false}
                                        value={
                                            selectedEventId
                                        }
                                        onChange={
                                            setSelectedEventId
                                        }
                                        data={events.map(
                                            (event) => ({
                                                value: String(
                                                    event.eventID
                                                ),
                                                label:
                                                    event.eventName,
                                            })
                                        )}
                                        disabled={
                                            loadingEvents
                                        }
                                        rightSection={
                                            loadingEvents ? (
                                                <Loader size="xs" />
                                            ) : undefined
                                        }
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6,
                                    }}
                                >
                                    <TextInput
                                        type="date"
                                        label="Træningsdato"
                                        value={practiceDate}
                                        onChange={(event) =>
                                            setPracticeDate(
                                                event
                                                    .currentTarget
                                                    .value
                                            )
                                        }
                                    />
                                </Grid.Col>
                            </Grid>

                            {selectedEvent && (
                                <Paper
                                    p="md"
                                    radius="md"
                                    bg="gray.0"
                                >
                                    <Group
                                        justify="space-between"
                                        align="center"
                                    >
                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Valgt event
                                            </Text>

                                            <Text fw={700}>
                                                {
                                                    selectedEvent.eventName
                                                }
                                            </Text>
                                        </div>

                                        {(selectedEvent.startTime ||
                                            selectedEvent.endTime) && (
                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    {formatEventDate(
                                                        selectedEvent.startTime
                                                    )}

                                                    {selectedEvent.endTime &&
                                                        ` – ${formatEventDate(
                                                            selectedEvent.endTime
                                                        )}`}
                                                </Text>
                                            )}
                                    </Group>
                                </Paper>
                            )}
                        </Stack>
                    </Paper>

                    {/* LOCATIONS */}

                    <Paper
                        withBorder
                        radius="md"
                        p="lg"
                    >
                        <Stack gap="lg">
                            <Group
                                justify="space-between"
                                align="flex-start"
                            >
                                <div>
                                    <Title order={3}>
                                        2. Opsæt haller
                                    </Title>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Angiv antal tilgængelige
                                        baner. En hal med 0 baner
                                        bliver ikke medtaget i
                                        planen.
                                    </Text>
                                </div>

                                {activeLocations.length >
                                    0 && (
                                        <Badge
                                            size="lg"
                                            variant="light"
                                        >
                                            {
                                                activeLocations.length
                                            }{" "}
                                            haller ·{" "}
                                            {totalCourts} baner
                                        </Badge>
                                    )}
                            </Group>

                            {loadingLocations ? (
                                <Group
                                    justify="center"
                                    py="xl"
                                >
                                    <Loader />

                                    <Text>
                                        Henter
                                        træningssteder...
                                    </Text>
                                </Group>
                            ) : locations.length === 0 ? (
                                <Alert
                                    color="yellow"
                                    title="Ingen træningssteder"
                                >
                                    Der er ikke oprettet
                                    træningssteder til det
                                    valgte event.
                                </Alert>
                            ) : (
                                <SimpleGrid
                                    cols={{
                                        base: 1,
                                        sm: 2,
                                    }}
                                    spacing="md"
                                >
                                    {locations.map(
                                        (location) => {
                                            const courts =
                                                getCourtCount(
                                                    location.value
                                                );

                                            const active =
                                                courts > 0;

                                            return (
                                                <Card
                                                    key={
                                                        location.value
                                                    }
                                                    withBorder
                                                    radius="md"
                                                    padding="lg"
                                                >
                                                    <Stack gap="md">
                                                        <Group
                                                            justify="space-between"
                                                            align="flex-start"
                                                        >
                                                            <div>
                                                                <Text
                                                                    fw={
                                                                        700
                                                                    }
                                                                    size="lg"
                                                                >
                                                                    {
                                                                        location.text
                                                                    }
                                                                </Text>

                                                                <Text
                                                                    size="sm"
                                                                    c="dimmed"
                                                                >
                                                                    Tilgængelige
                                                                    baner
                                                                </Text>
                                                            </div>

                                                            <Badge
                                                                color={
                                                                    active
                                                                        ? "green"
                                                                        : "gray"
                                                                }
                                                                variant="light"
                                                            >
                                                                {active
                                                                    ? "Med i planen"
                                                                    : "Ikke valgt"}
                                                            </Badge>
                                                        </Group>

                                                        <NumberInput
                                                            label="Antal baner"
                                                            placeholder="0"
                                                            min={0}
                                                            max={50}
                                                            value={
                                                                courts
                                                            }
                                                            onChange={(
                                                                value
                                                            ) =>
                                                                updateLocationCourts(
                                                                    location.value,
                                                                    value
                                                                )
                                                            }
                                                        />
                                                    </Stack>
                                                </Card>
                                            );
                                        }
                                    )}
                                </SimpleGrid>
                            )}
                        </Stack>
                    </Paper>

                    {/* SUMMARY */}

                    <Paper
                        withBorder
                        radius="md"
                        p="lg"
                    >
                        <Stack gap="md">
                            <Title order={3}>
                                3. Opret planen
                            </Title>

                            <Grid>
                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 4,
                                    }}
                                >
                                    <Text
                                        size="xs"
                                        c="dimmed"
                                    >
                                        Event
                                    </Text>

                                    <Text fw={600}>
                                        {selectedEvent
                                            ?.eventName ||
                                            "Ikke valgt"}
                                    </Text>
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 4,
                                    }}
                                >
                                    <Text
                                        size="xs"
                                        c="dimmed"
                                    >
                                        Dato
                                    </Text>

                                    <Text fw={600}>
                                        {practiceDate
                                            ? new Date(
                                                `${practiceDate}T00:00:00`
                                            ).toLocaleDateString(
                                                "da-DK"
                                            )
                                            : "Ikke valgt"}
                                    </Text>
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 4,
                                    }}
                                >
                                    <Text
                                        size="xs"
                                        c="dimmed"
                                    >
                                        Kapacitet
                                    </Text>

                                    <Text fw={600}>
                                        {
                                            activeLocations.length
                                        }{" "}
                                        haller ·{" "}
                                        {totalCourts} baner
                                    </Text>
                                </Grid.Col>
                            </Grid>

                            {activeLocations.length >
                                0 && (
                                    <Stack gap="xs">
                                        {activeLocations.map(
                                            (item) => {
                                                const location =
                                                    locations.find(
                                                        (x) =>
                                                            x.value ===
                                                            item.practiceLocationId
                                                    );

                                                return (
                                                    <Group
                                                        key={
                                                            item.practiceLocationId
                                                        }
                                                        justify="space-between"
                                                    >
                                                        <Text>
                                                            {location?.text ??
                                                                `Location ${item.practiceLocationId}`}
                                                        </Text>

                                                        <Badge
                                                            variant="light"
                                                            size="lg"
                                                        >
                                                            {
                                                                item.numberOfCourts
                                                            }{" "}
                                                            baner
                                                        </Badge>
                                                    </Group>
                                                );
                                            }
                                        )}
                                    </Stack>
                                )}

                            <Group
                                justify="flex-end"
                                mt="md"
                            >
                                <Button
                                    variant="default"
                                    onClick={() =>
                                        navigate(
                                            "/Admin/Practice"
                                        )
                                    }
                                >
                                    Annuller
                                </Button>

                                <Button
                                    size="md"
                                    onClick={createPlan}
                                    loading={saving}
                                    disabled={
                                        loadingLocations ||
                                        !selectedEventId ||
                                        !practiceDate ||
                                        activeLocations.length ===
                                        0
                                    }
                                >
                                    Opret og åbn plan
                                </Button>
                            </Group>
                        </Stack>
                    </Paper>
                </Stack>
            </Container>
        </div>
    );
};

export default PracticePlannerCreatePage;